import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BillingHistoryItem, BillingPlan, QuoteResponse } from '@pomoshnik/shared';
import { ApiClientError } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/lib/stores/authStore';
import { SubscriptionScreenView, type AsyncState, type QuoteState } from './SubscriptionScreenView';

/**
 * Container Экрана 2.11.3 «Подписка / оплата» — Block 11 Phase 11.C (ADR-0093, ADR-0005).
 *
 * Держит state + side effects; UI — чистый presenter `SubscriptionScreenView`.
 *
 * **ДЕНЬГИ (P-006/P-003):** контейнер НЕ считает суммы и НЕ шлёт их. `quote`/
 * `checkout` уходят с `planCode` (+`promoCode?`) — сумму и скидку считает бэк (мок).
 * Кнопка «Оплатить/Продлить» — **заглушка** (`paymentStubOpen`), реальный
 * `apiClient.billing.checkout` в 11.C НЕ вызывается (реальный checkout — sandbox-бэк).
 *
 * **Источники данных (моки):**
 *   - `authStore.subscription` + mount-fetch `GET /api/me` при subscription===null
 *     (mirror CabinetHub 11.A; на ошибке — fallback, без logout).
 *   - `GET /api/billing/plans?region` — тариф региона.
 *   - `POST /api/billing/quote` — промо-preview по «Применить».
 *   - `GET /api/billing/history` — история (пусто → «Платежей пока нет»).
 *
 * **Transient `?promo=` (R3):** если пришли с хаба «Продлить заранее −30%»
 * (`/cabinet/subscription?promo=AUTORENEW_3D`) — поле пред-заполняется и URL
 * очищается через `setSearchParams({}, { replace: true })` (не bookmark-able,
 * back не должен «возвращать» промо).
 *
 * **5 состояний (P-021)** для plans / quote / history.
 */

const GENERIC_QUOTE_ERROR = 'Не удалось проверить промокод. Попробуйте ещё раз.';
const NETWORK_QUOTE_ERROR = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';

export function SubscriptionScreen() {
  const [searchParams, setSearchParams] = useSearchParams();

  const subscription = useAuthStore((s) => s.subscription);
  const setSubscription = useAuthStore((s) => s.setSubscription);
  const region = useAuthStore((s) => s.region);
  const jwt = useAuthStore((s) => s.jwt);
  const getDaysRemaining = useAuthStore((s) => s.getDaysRemaining);

  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const [planState, setPlanState] = useState<AsyncState>('idle');
  const [plan, setPlan] = useState<BillingPlan | null>(null);

  const [historyState, setHistoryState] = useState<AsyncState>('idle');
  const [history, setHistory] = useState<ReadonlyArray<BillingHistoryItem>>([]);

  // Промо: pre-fill из ?promo= (transient, R3) — читаем один раз при инициализации.
  const initialPromoRef = useRef<string>(searchParams.get('promo') ?? '');
  const [promoCode, setPromoCode] = useState<string>(initialPromoRef.current);
  const [quoteState, setQuoteState] = useState<QuoteState>('idle');
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoteError, setQuoteError] = useState<string | undefined>();

  const [paymentStubOpen, setPaymentStubOpen] = useState(false);

  // Transient `?promo=` (R3): очищаем из URL после consume, чтобы back/refresh
  // не «возвращал» промо (не bookmark-able intent).
  useEffect(() => {
    if (searchParams.has('promo')) {
      const next = new URLSearchParams(searchParams);
      next.delete('promo');
      setSearchParams(next, { replace: true });
    }
    // только при первом mount — initialPromoRef уже зафиксировал значение.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mount-fetch подписки при прямом входе/refresh (subscription не persist — P-009).
  useEffect(() => {
    if (jwt === null || subscription !== null) return;
    let cancelled = false;
    setSubscriptionLoading(true);
    apiClient.me
      .get()
      .then((me) => {
        if (cancelled) return;
        setSubscription({
          status: me.subscriptionStatus,
          trialStartedAt: me.trialStartedAt,
          expiresAt: me.expiresAt,
        });
      })
      .catch(() => {
        // 401 / network / parse — fallback-карточка, без logout.
      })
      .finally(() => {
        if (!cancelled) setSubscriptionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, subscription, setSubscription]);

  // Mount-fetch тарифа по региону.
  useEffect(() => {
    if (region === null) {
      // Регион не установлен (degraded) — тариф недоступен.
      setPlanState('empty');
      return;
    }
    let cancelled = false;
    setPlanState('loading');
    apiClient.billing
      .plans(region)
      .then((res) => {
        if (cancelled) return;
        const first = res.plans[0] ?? null;
        setPlan(first);
        setPlanState(first ? 'success' : 'empty');
      })
      .catch(() => {
        if (!cancelled) setPlanState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [region]);

  // Mount-fetch истории платежей.
  useEffect(() => {
    if (jwt === null) {
      setHistoryState('empty');
      return;
    }
    let cancelled = false;
    setHistoryState('loading');
    apiClient.billing
      .history()
      .then((res) => {
        if (cancelled) return;
        setHistory(res.items);
        setHistoryState(res.items.length > 0 ? 'success' : 'empty');
      })
      .catch(() => {
        if (!cancelled) setHistoryState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [jwt]);

  const handleApplyPromo = useCallback(() => {
    const code = promoCode.trim();
    if (code.length === 0 || plan === null) return;
    setQuoteState('loading');
    setQuoteError(undefined);
    // ДЕНЬГИ: шлём ТОЛЬКО planCode + promoCode — сумму считает бэк (P-006/P-003).
    apiClient.billing
      .quote({ planCode: plan.planCode, promoCode: code })
      .then((res) => {
        setQuote(res);
        setQuoteState('success');
      })
      .catch((err) => {
        setQuoteState('error');
        setQuoteError(
          err instanceof ApiClientError && err.code === 'NETWORK_ERROR'
            ? NETWORK_QUOTE_ERROR
            : GENERIC_QUOTE_ERROR,
        );
      });
  }, [promoCode, plan]);

  const handlePromoCodeChange = useCallback((v: string) => {
    setPromoCode(v);
    // Сброс предыдущего результата при правке кода (UI чистый до повторного «Применить»).
    setQuoteState('idle');
    setQuote(null);
    setQuoteError(undefined);
  }, []);

  // ЗАГЛУШКА оплаты: НЕ вызываем реальный checkout (P-024; sandbox-бэк позже).
  const handlePay = useCallback(() => {
    setPaymentStubOpen(true);
  }, []);

  return (
    <SubscriptionScreenView
      subscription={subscription}
      subscriptionLoading={subscriptionLoading}
      daysRemaining={getDaysRemaining()}
      planState={planState}
      plan={plan}
      promoCode={promoCode}
      onPromoCodeChange={handlePromoCodeChange}
      quoteState={quoteState}
      quote={quote}
      quoteError={quoteError}
      onApplyPromo={handleApplyPromo}
      onPay={handlePay}
      paymentStubOpen={paymentStubOpen}
      historyState={historyState}
      history={history}
    />
  );
}
